import { observable } from '@legendapp/state';
import { supabase } from '../lib/supabase';
import { auth$ } from './auth$';

// Chats state
export const chats$ = observable({
  items: [],           // All chats for current language
  activeChatId: null,  // Currently selected chat ID
  isLoading: false,    // Start with false - will be set true when fetching
});

// Fetch chats for a specific language
export const fetchChats = async (languageCode) => {
  const user = auth$.user.get();
  if (!user || !languageCode) {
    chats$.items.set([]);
    chats$.activeChatId.set(null);  // Clear active chat when no language
    chats$.isLoading.set(false);
    return;
  }

  chats$.isLoading.set(true);

  // Clear active chat when switching languages (prevents stale ID from other language)
  chats$.activeChatId.set(null);

  const { data, error } = await supabase
    .from('mellow_chats')
    .select('*')
    .eq('user_id', user.id)
    .eq('language_code', languageCode)
    .eq('deleted', false)
    .order('pinned', { ascending: false })
    .order('updated_at', { ascending: false });

  if (error) {
    console.error('Failed to fetch chats:', error);
    chats$.isLoading.set(false);
    return;
  }

  chats$.items.set(data || []);

  // Don't auto-select any chat - always show new chat screen
  // activeChatId remains null - user can select from dropdown if needed

  chats$.isLoading.set(false);
};

// Create a new chat
export const createChat = async (languageCode, firstMessage = null, initialMode = null) => {
  const user = auth$.user.get();
  if (!user) throw new Error('Not authenticated');

  // Guard: Ensure languageCode is provided
  // Prevents null constraint violation on mellow_chats.language_code
  if (!languageCode) {
    throw new Error('Language code is required to create a chat');
  }

  const title = firstMessage
    ? firstMessage.slice(0, 50) + (firstMessage.length > 50 ? '...' : '')
    : 'New Chat';

  const messages = firstMessage
    ? [{ role: 'user', content: firstMessage, timestamp: Date.now() }]
    : [];

  const { data, error } = await supabase
    .from('mellow_chats')
    .insert({
      user_id: user.id,
      language_code: languageCode,
      title,
      messages,
      initial_mode: initialMode, // Store the mode when chat was created
    })
    .select()
    .single();

  if (error) throw error;

  // Add to local state and select it
  const items = chats$.items.get();
  chats$.items.set([data, ...items]);
  chats$.activeChatId.set(data.id);

  return data;
};

// Add message to a chat (with optional source for dict/grammar)
export const addMessage = async (chatId, message, source = null) => {
  const items = chats$.items.get();
  const chat = items.find(c => c.id === chatId);
  if (!chat) throw new Error('Chat not found');

  const newMessages = [...(chat.messages || []), message];
  const messageIndex = newMessages.length - 1;

  // Update title if this is the first user message
  const isFirstUserMessage = !chat.messages?.some(m => m.role === 'user');
  const updates = {
    messages: newMessages,
    updated_at: new Date().toISOString(),
  };

  if (isFirstUserMessage && message.role === 'user') {
    updates.title = message.content.slice(0, 50) + (message.content.length > 50 ? '...' : '');
  }

  // Add message source if provided (for dict/grammar capsules)
  if (source) {
    const existingSources = chat.message_sources || {};
    updates.message_sources = { ...existingSources, [messageIndex]: source };
  }

  const { error } = await supabase
    .from('mellow_chats')
    .update(updates)
    .eq('id', chatId);

  if (error) throw error;

  // Update local state
  chats$.items.set(items.map(c =>
    c.id === chatId ? { ...c, ...updates } : c
  ));

  return messageIndex;
};

// Get active chat
export const getActiveChat = () => {
  const id = chats$.activeChatId.get();
  const items = chats$.items.get();
  return items.find(c => c.id === id) || null;
};

// Set active chat
export const setActiveChat = (chatId) => {
  chats$.activeChatId.set(chatId);
};

// Delete a chat (soft delete)
export const deleteChat = async (chatId) => {
  const { error } = await supabase
    .from('mellow_chats')
    .update({ deleted: true, updated_at: new Date().toISOString() })
    .eq('id', chatId);

  if (error) throw error;

  // Remove from local state
  const items = chats$.items.get();
  chats$.items.set(items.filter(c => c.id !== chatId));

  // Clear active if deleted
  if (chats$.activeChatId.get() === chatId) {
    const remaining = items.filter(c => c.id !== chatId);
    chats$.activeChatId.set(remaining[0]?.id || null);
  }
};

// Clear active chat (for new chat)
export const clearActiveChat = () => {
  chats$.activeChatId.set(null);
};

// Truncate messages from a given index (for retry/delete)
export const truncateMessagesFromIndex = async (chatId, fromIndex) => {
  const items = chats$.items.get();
  const chat = items.find(c => c.id === chatId);
  if (!chat) throw new Error('Chat not found');

  // Truncate messages array
  const truncatedMessages = (chat.messages || []).slice(0, fromIndex);

  // Truncate message_sources - remove keys >= fromIndex
  const truncatedSources = {};
  if (chat.message_sources) {
    Object.keys(chat.message_sources).forEach(key => {
      const idx = parseInt(key, 10);
      if (idx < fromIndex) {
        truncatedSources[key] = chat.message_sources[key];
      }
    });
  }

  const updates = {
    messages: truncatedMessages,
    message_sources: truncatedSources,
    updated_at: new Date().toISOString(),
  };

  const { error } = await supabase
    .from('mellow_chats')
    .update(updates)
    .eq('id', chatId);

  if (error) throw error;

  // Update local state
  chats$.items.set(items.map(c =>
    c.id === chatId ? { ...c, ...updates } : c
  ));

  return truncatedMessages;
};

// Pin/unpin a chat
export const pinChat = async (chatId) => {
  const items = chats$.items.get();
  const chat = items.find(c => c.id === chatId);
  if (!chat) throw new Error('Chat not found');

  const newPinnedState = !chat.pinned;

  const { error } = await supabase
    .from('mellow_chats')
    .update({ pinned: newPinnedState, updated_at: new Date().toISOString() })
    .eq('id', chatId);

  if (error) throw error;

  // Update local state
  chats$.items.set(items.map(c =>
    c.id === chatId ? { ...c, pinned: newPinnedState } : c
  ));

  // Re-sort items (pinned first, then by updated_at)
  const sortedItems = [...chats$.items.get()].sort((a, b) => {
    if (a.pinned !== b.pinned) return b.pinned ? 1 : -1;
    return new Date(b.updated_at) - new Date(a.updated_at);
  });
  chats$.items.set(sortedItems);
};

// Get chats as dropdown options (with metadata for icons and actions)
export const getChatsAsOptions = () => {
  const items = chats$.items.get() || [];
  return items.map(c => ({
    value: c.id,
    label: c.title || 'New Chat',
    initialMode: c.initial_mode,
    pinned: c.pinned || false,
  }));
};

